import { useState, useEffect, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Palette, Save, Trash2, UserCircle, ImagePlus, Sparkles, Facebook, Instagram, Twitter, Rss, Share2 } from "lucide-react";
import { extractPalette } from "@/lib/color";

export default function BrandKit() {
  const [kit, setKit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const logoImgRef = useRef(null);

  useEffect(() => {
    api.get("/brand-kit").then((r) => {
      setKit(r.data);
      setReadOnly(!!r.data.read_only);
    });
  }, []);

  const update = (patch) => setKit((k) => ({ ...k, ...patch }));

  const readFile = (file, cb, max = 4) => {
    if (!file) return;
    if (file.size > max * 1024 * 1024) { toast.error(`Fichier trop lourd (max ${max} Mo).`); return; }
    const reader = new FileReader();
    reader.onload = () => cb(reader.result);
    reader.readAsDataURL(file);
  };

  const [consent, setConsent] = useState(false);

  const addPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!consent) { toast.error("Cochez d'abord la case de consentement."); return; }
    if ((kit.photos || []).length >= 5) { toast.error("Maximum 5 photos."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Photo trop lourde (max 5 Mo)."); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update({ photos: [...(kit.photos || []), data.url] });
      toast.success("Photo ajoutée.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removePhoto = (url) => update({ photos: (kit.photos || []).filter((u) => u !== url) });

  const pickLogo = (e) => {
    readFile(e.target.files?.[0], (dataUrl) => {
      update({ logo_data: dataUrl });
      const img = new Image();
      img.onload = () => {
        try {
          const { primary, secondary } = extractPalette(img);
          update({ logo_data: dataUrl, primary_color: primary, secondary_color: secondary });
          toast.success("Couleurs extraites du logo. Ajustez-les si besoin.");
        } catch (err) {}
      };
      img.src = dataUrl;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/brand-kit", {
        name: kit.name || "",
        logo_data: kit.logo_data || null,
        primary_color: kit.primary_color,
        secondary_color: kit.secondary_color,
        portrait_data: kit.portrait_data || null,
        baseline: kit.baseline || "",
        palette: kit.palette || "",
        photos: kit.photos || [],
        facebook_page_url: kit.facebook_page_url || "",
        instagram_handle: kit.instagram_handle || "",
        x_handle: kit.x_handle || "",
        x_premium: !!kit.x_premium,
        substack_url: kit.substack_url || "",
      });
      toast.success("Marque enregistrée. Vos visuels utiliseront ces réglages.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!kit) return <div className="p-12 text-ink-light/60">Chargement…</div>;

  const inputCls = "w-full border border-border rounded-lg px-3.5 py-2.5 text-sm outline-none focus:ring-1 focus:ring-coral focus:border-coral transition-colors bg-white";
  const labelCls = "text-xs font-bold uppercase tracking-[0.12em] text-ink-light/70";

  return (
    <div className="p-8 lg:p-12 max-w-3xl">
      <div className="mb-2 flex items-center gap-2 text-coral-dark">
        <Palette className="h-4 w-4" />
        <span className="text-xs font-bold uppercase tracking-[0.2em]">Ma marque</span>
      </div>
      <h1 className="font-display font-bold text-4xl text-ink mb-3">Votre brand kit</h1>
      <p className="text-ink-light mb-8 max-w-2xl leading-relaxed">
        Tous vos visuels (gabarits et illustrations IA) se construisent à partir de ces éléments. Sans configuration,
        un rendu neutre et élégant est appliqué par défaut.
      </p>

      {readOnly && (
        <div className="bg-coral-soft border border-coral/30 rounded-lg p-4 mb-6 flex items-center gap-2 text-sm text-coral-dark">
          <Sparkles className="h-4 w-4" /> Votre marque est fournie par votre organisation{kit.org_name ? ` (${kit.org_name})` : ""} et n'est pas modifiable.
        </div>
      )}

      <fieldset disabled={readOnly} className={readOnly ? "opacity-70 pointer-events-none space-y-6" : "space-y-6"}>
        <div className="bg-white border border-border rounded-xl p-6 grid md:grid-cols-2 gap-6">
          <div>
            <label className={labelCls}>Logo (PNG transparent, optionnel)</label>
            <div className="mt-2">
              {kit.logo_data ? (
                <div className="relative inline-block">
                  <div className="w-40 h-24 flex items-center justify-center border border-border rounded-lg bg-[repeating-conic-gradient(#f3f3f3_0%_25%,#fff_0%_50%)] bg-[length:16px_16px] p-2">
                    <img ref={logoImgRef} src={kit.logo_data} alt="logo" className="max-h-full max-w-full object-contain" />
                  </div>
                  <button data-testid="remove-logo-btn" onClick={() => update({ logo_data: null })}
                    className="absolute -top-2 -right-2 bg-white border border-border rounded-full p-1 text-ink-light hover:text-destructive transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <label data-testid="logo-upload-label" className="w-40 h-24 flex flex-col items-center justify-center gap-1 border border-dashed border-border rounded-lg text-ink-light/60 cursor-pointer hover:border-coral hover:text-coral transition-colors text-[11px]">
                  <ImagePlus className="h-5 w-5" /> Logo
                  <input data-testid="logo-upload-input" type="file" accept="image/png,image/svg+xml,image/*" onChange={pickLogo} className="hidden" />
                </label>
              )}
            </div>
          </div>
          <div>
            <label className={labelCls}>Photo portrait (optionnel)</label>
            <div className="mt-2 flex items-center gap-3">
              {kit.portrait_data ? (
                <div className="relative">
                  <img src={kit.portrait_data} alt="portrait" className="w-24 h-24 object-cover rounded-lg border border-border" />
                  <button data-testid="remove-portrait-btn" onClick={() => update({ portrait_data: null })}
                    className="absolute -top-2 -right-2 bg-white border border-border rounded-full p-1 text-ink-light hover:text-destructive transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <label data-testid="kit-portrait-label" className="w-24 h-24 flex flex-col items-center justify-center gap-1 border border-dashed border-border rounded-lg text-ink-light/60 cursor-pointer hover:border-coral hover:text-coral transition-colors text-[11px]">
                  <UserCircle className="h-6 w-6" /> Photo
                  <input data-testid="kit-portrait-input" type="file" accept="image/*" onChange={(e) => readFile(e.target.files?.[0], (d) => update({ portrait_data: d }))} className="hidden" />
                </label>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white border border-border rounded-xl p-6">
          <label className={labelCls}>Couleurs de marque</label>
          <p className="text-xs text-ink-light/60 mt-1 mb-4">Extraites automatiquement du logo si vous en importez un, et modifiables.</p>
          <div className="flex flex-wrap gap-6">
            {[["Primaire", "primary_color", "primary-color-input"], ["Secondaire", "secondary_color", "secondary-color-input"]].map(([lbl, key, tid]) => (
              <div key={key} className="flex items-center gap-3">
                <input type="color" data-testid={tid} value={kit[key]} onChange={(e) => update({ [key]: e.target.value })}
                  className="w-12 h-12 rounded-lg border border-border cursor-pointer bg-white" />
                <div>
                  <p className="text-sm font-semibold text-ink">{lbl}</p>
                  <input value={kit[key]} onChange={(e) => update({ [key]: e.target.value })}
                    className="text-xs text-ink-light border border-border rounded px-2 py-1 w-24 mt-0.5 uppercase" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-border rounded-xl p-6 space-y-5">
          <div>
            <label className={labelCls}>Nom affiché sur les visuels</label>
            <input data-testid="kit-name-input" value={kit.name || ""} onChange={(e) => update({ name: e.target.value })}
              className={`${inputCls} mt-1.5`} placeholder="Ex : Gilles IACONES" />
          </div>
          <div>
            <label className={labelCls}>Baseline (optionnel)</label>
            <input data-testid="kit-baseline-input" value={kit.baseline || ""} onChange={(e) => update({ baseline: e.target.value })}
              className={`${inputCls} mt-1.5`} placeholder="Ex : Dirigeant & conférencier" />
          </div>
          <div>
            <label className={labelCls}>Palette du mode « Image du post »</label>
            <p className="text-xs text-ink-light/70 mt-1">Décrit vos couleurs de marque, injectées dans le prompt (variable {`{{PALETTE}}`}). Ex : « orange intense ; doré ».</p>
            <input data-testid="kit-palette-input" value={kit.palette || ""} onChange={(e) => update({ palette: e.target.value })}
              className={`${inputCls} mt-1.5`} placeholder="orange intense ; doré" />
          </div>
          <div>
            <label className={labelCls}>Mes photos, pour vous mettre en scène dans vos visuels</label>
            <p className="text-xs text-ink-light/70 mt-1">Jusqu'à 5 photos de vous (portraits, situations variées), 5 Mo max. Utilisées comme références quand « M'inclure » est activé. Uniquement des photos de vous.</p>
            <label className="mt-2 flex items-start gap-2 text-xs text-ink-light">
              <input type="checkbox" data-testid="kit-photos-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
              <span>Ces photos me représentent et j'en détiens les droits.</span>
            </label>
            <div className="mt-3 flex flex-wrap gap-3">
              {(kit.photos || []).map((url) => (
                <div key={url} className="relative w-20 h-20 rounded-lg overflow-hidden border border-border group">
                  <img src={api.defaults.baseURL.replace(/\/api$/, "") + url + "?token=" + (localStorage.getItem("apollo_token") || "")} alt="photo" className="w-full h-full object-cover" data-testid="kit-photo-thumb" />
                  <button onClick={() => removePhoto(url)} data-testid="kit-photo-remove"
                    className="absolute top-1 right-1 bg-black/60 text-white rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {(kit.photos || []).length < 5 && !readOnly && (
                <label data-testid="kit-photo-add" className={`w-20 h-20 rounded-lg border-2 border-dashed flex items-center justify-center cursor-pointer transition-colors ${consent ? "border-coral/50 hover:border-coral text-coral" : "border-border text-ink-light/40 cursor-not-allowed"}`}>
                  <ImagePlus className="h-6 w-6" />
                  <input type="file" accept="image/*" className="hidden" onChange={addPhoto} disabled={!consent} />
                </label>
              )}
            </div>
          </div>
        </div>
      </fieldset>

      {/* Comptes réseaux = données personnelles : toujours éditables, même si la marque est gérée par l'organisation. */}
      <div className="bg-white border border-border rounded-xl p-6 space-y-5 mt-6">
          <div className="flex items-center gap-2">
            <Share2 className="h-4 w-4 text-coral-dark" />
            <label className={labelCls}>Mes autres réseaux</label>
          </div>
          <p className="text-xs text-ink-light/70 -mt-2 leading-relaxed">
            Pour décliner vos posts LinkedIn vers d'autres réseaux. Aucun champ n'est obligatoire : Pulse publie
            automatiquement sur LinkedIn et vous assiste pour copier, télécharger et ouvrir les autres réseaux.
          </p>

          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-ink">
              <Facebook className="h-4 w-4 text-[#1877F2]" /> Facebook
            </label>
            <input data-testid="input-facebook-url" value={kit.facebook_page_url || ""}
              onChange={(e) => update({ facebook_page_url: e.target.value })}
              className={`${inputCls} mt-1.5`} placeholder="https://www.facebook.com/ma-page" />
            <p className="text-xs text-ink-light/60 mt-1">L'adresse de votre Page Facebook, pas de votre profil personnel.</p>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-ink">
              <Instagram className="h-4 w-4 text-[#E4405F]" /> Instagram
            </label>
            <input data-testid="input-instagram-handle" value={kit.instagram_handle || ""}
              onChange={(e) => update({ instagram_handle: e.target.value })}
              className={`${inputCls} mt-1.5`} placeholder="mon.compte" />
            <p className="text-xs text-ink-light/60 mt-1">Votre nom de compte. Pour publier une image générée, un compte professionnel ou créateur est recommandé.</p>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-ink">
              <Twitter className="h-4 w-4 text-ink" /> X
            </label>
            <input data-testid="input-x-handle" value={kit.x_handle || ""}
              onChange={(e) => update({ x_handle: e.target.value })}
              className={`${inputCls} mt-1.5`} placeholder="moncompte" />
            <p className="text-xs text-ink-light/60 mt-1">Votre nom de compte.</p>
            <label className="mt-2 flex items-start gap-2 text-xs text-ink-light">
              <input type="checkbox" data-testid="checkbox-x-premium" checked={!!kit.x_premium}
                onChange={(e) => update({ x_premium: e.target.checked })} className="mt-0.5" />
              <span>Cochez si vous avez un abonnement X Premium : vos posts pourront alors dépasser 280 caractères.</span>
            </label>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-ink">
              <Rss className="h-4 w-4 text-[#FF6719]" /> Substack
            </label>
            <input data-testid="input-substack-url" value={kit.substack_url || ""}
              onChange={(e) => update({ substack_url: e.target.value })}
              className={`${inputCls} mt-1.5`} placeholder="https://mapublication.substack.com" />
            <p className="text-xs text-ink-light/60 mt-1">L'adresse de votre publication Substack.</p>
          </div>
      </div>

      <div className="mt-8">
        <button data-testid="save-kit-btn" onClick={save} disabled={busy}
          className="bg-coral-strong text-white px-6 py-3 rounded-lg text-sm font-bold hover:bg-coral-hover transition-colors disabled:opacity-60 flex items-center gap-2">
          <Save className="h-4 w-4" /> {busy ? "Enregistrement…" : (readOnly ? "Enregistrer mes réseaux" : "Enregistrer ma marque")}
        </button>
      </div>
    </div>
  );
}
